/**
 * Owner-scoped drafts, held in memory only.
 *
 * IMPLEMENTATION-CONTRACTS §6: *"Logout or owner change clears cache/drafts; 401 hides
 * cached financial data and suspends the in-memory draft until the same owner
 * reauthenticates, without persisting it."* This is that store, with its two rules made
 * structural rather than remembered:
 *
 * - every entry is keyed by the **owner** as well as by the draft's own key, so an
 *   entry belonging to one owner can never be read for another;
 * - nothing here touches `localStorage`, `sessionStorage`, `IndexedDB` or a cookie —
 *   the map is a JavaScript value, and it dies with the tab.
 *
 * The entry editor of P4 will hold its financial draft here. P2-T2 holds the shell's
 * own draft: the identifier of the sign-in form, so a session that ends can offer the
 * same owner their own typed identifier back without ever writing it down.
 */

export interface InMemoryDrafts {
  set(ownerKey: string, key: string, value: string): void;
  get(ownerKey: string, key: string): string | undefined;
  /** Drop everything belonging to one owner — the owner-switch rule. */
  clearOwner(ownerKey: string): void;
  /** Drop everything — the logout rule. */
  clear(): void;
  readonly size: number;
}

/** One draft slot: `ownerKey` cannot collide with a key that contains the separator. */
function slot(ownerKey: string, key: string): string {
  return `${ownerKey}\u0000${key}`;
}

export function createInMemoryDrafts(): InMemoryDrafts {
  const entries = new Map<string, string>();
  return {
    set(ownerKey, key, value) {
      entries.set(slot(ownerKey, key), value);
    },
    get(ownerKey, key) {
      return entries.get(slot(ownerKey, key));
    },
    clearOwner(ownerKey) {
      const prefix = `${ownerKey}\u0000`;
      for (const entry of [...entries.keys()]) {
        if (entry.startsWith(prefix)) entries.delete(entry);
      }
    },
    clear() {
      entries.clear();
    },
    get size() {
      return entries.size;
    },
  };
}

/** The draft key the shell uses for the sign-in form's identifier. */
export const SIGN_IN_IDENTIFIER_DRAFT = 'sign-in.identifier';
